import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../api/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { isProductionHost } from '../utils/environment';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState('login'); // 'login', 'otp', 'forgot_password', 'reset_password'
  const [formData, setFormData] = useState({ 
    email: '', 
    password: '', 
    rememberMe: true,
    otp: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [devLogs, setDevLogs] = useState([]);

  // Environment detection — see /app/frontend/src/utils/environment.js
  const isProduction = isProductionHost();
  const isDevelopment = !isProduction;

  // Demo accounts for quick login - EMZI Theme (only shown in development)
  const demoAccounts = [
    { email: 'demo_executor@emzi.digital', role: 'Executor', color: 'bg-emzi-blue' },
    { email: 'demo_supervisor@emzi.digital', role: 'Supervisor', color: 'bg-emzi-blue' },
    { email: 'demo_management@emzi.digital', role: 'Management', color: 'bg-emzi-blue' },
    { email: 'demo_admin@emzi.digital', role: 'Admin', color: 'bg-emzi-blue' }
  ];

  // Add to dev log (DEV_LOG_ONLY mode display)
  const addToDevLog = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setDevLogs(prev => [...prev.slice(-4), `[${timestamp}] ${message}`]);
  };

  useEffect(() => {
    addToDevLog('NEXUS V6.2 Phase 3 Login UI loaded - DEV_LOG_ONLY mode active');
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      addToDevLog(`Attempting login for: ${formData.email}`);
      const response = await authApi.login({
        email: formData.email,
        password: formData.password,
        remember_me: formData.rememberMe
      });

      if (response.requires_otp) {
        addToDevLog(`OTP required for ${response.email} - switching to OTP step`);
        setSuccess('OTP sent to your email. Check the dev log below for the code.');
        setStep('otp');
      } else {
        addToDevLog(`Login successful for ${response.user.email} as ${response.user.role}`);
        login(response);
        
        // Check if user must change password
        if (response.user.must_change_password) {
          addToDevLog(`User must change password - redirecting to change password page`);
          navigate('/change-password');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Login failed. Please try again.';
      setError(errorMsg);
      addToDevLog(`Login failed: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      addToDevLog(`Verifying OTP: ${formData.otp} for ${formData.email}`);
      const response = await authApi.verifyOtp({
        email: formData.email,
        otp_code: formData.otp
      });

      addToDevLog(`OTP verification successful for ${response.user.email}`);
      login(response);
      
      // Check if user must change password
      if (response.user.must_change_password) {
        addToDevLog(`User must change password - redirecting to change password page`);
        navigate('/change-password');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'OTP verification failed. Please try again.';
      setError(errorMsg);
      addToDevLog(`OTP verification failed: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      addToDevLog(`Requesting password reset for: ${formData.email}`);
      await authApi.forgotPassword({ email: formData.email });
      addToDevLog(`Password reset OTP sent to ${formData.email} (check dev log)`);
      setSuccess('Password reset OTP sent to your email. Check the dev log below for the code.');
      setStep('reset_password');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Password reset request failed.';
      setError(errorMsg);
      addToDevLog(`Password reset failed: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      addToDevLog(`Resetting password for: ${formData.email}`);
      await authApi.resetPassword({
        email: formData.email,
        otp_code: formData.otp,
        new_password: formData.newPassword
      });

      addToDevLog(`Password reset successful for ${formData.email}`);
      setSuccess('Password reset successful. You can now login with your new password.');
      setStep('login');
      setFormData(prev => ({ ...prev, password: '', otp: '', newPassword: '', confirmPassword: '' }));
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Password reset failed.';
      setError(errorMsg);
      addToDevLog(`Password reset failed: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async (demoEmail) => {
    setFormData(prev => ({ ...prev, email: demoEmail, password: 'emzitest123' }));
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      addToDevLog(`Demo login: ${demoEmail} with password: emzitest123`);
      const response = await authApi.demoLogin({
        email: demoEmail,
        password: 'emzitest123'
      });

      // Tag session as DEMO
      const demoSession = {
        ...response,
        user: {
          ...response.user,
          isDemoMode: true
        }
      };

      const ttlHours = response.expires_in / 3600;
      addToDevLog(`✅ Demo login successful - Role: ${response.user.role} (DEMO MODE)`);
      addToDevLog(`🔑 Access token valid for ${ttlHours}h, Refresh token: ${response.refresh_token ? 'Yes' : 'No'}`);
      login(demoSession);
      
      // Demo users should not have must_change_password, but check anyway
      if (response.user.must_change_password) {
        navigate('/change-password');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Demo login failed.';
      setError(errorMsg);
      addToDevLog(`❌ Demo login failed: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const renderLoginForm = () => (
    <form onSubmit={handleLogin} className="space-y-4">
      <div className="space-y-1.5">
        <label className="text-sm font-semibold text-gray-700" htmlFor="email">Email</label>
        <Input
          id="email"
          type="email"
          placeholder="your@email.com"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
          data-testid="email-input"
          className="h-12 text-base border-2 border-gray-200 focus:border-[#0e67a2] rounded-xl bg-gray-50 focus:bg-white transition-all"
        />
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-semibold text-gray-700" htmlFor="password">Password</label>
        <Input
          id="password"
          type="password"
          placeholder="••••••••"
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          required
          data-testid="password-input"
          className="h-12 text-base border-2 border-gray-200 focus:border-[#0e67a2] rounded-xl bg-gray-50 focus:bg-white transition-all"
        />
      </div>

      <div className="flex items-center justify-between py-1">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="remember"
            checked={formData.rememberMe}
            onCheckedChange={(checked) => setFormData({ ...formData, rememberMe: checked })}
            className="border-gray-300 data-[state=checked]:bg-[#0e67a2] data-[state=checked]:border-[#0e67a2]"
          />
          <label htmlFor="remember" className="text-sm text-gray-600">Remember me</label>
        </div>
        <button
          type="button"
          onClick={() => setStep('forgot_password')}
          className="text-sm text-[#FF7B00] hover:text-orange-600 hover:underline font-semibold"
        >
          Forgot password?
        </button>
      </div>

      <Button 
        type="submit" 
        className="w-full h-14 text-white font-bold text-lg rounded-xl border-0 transition-all duration-300 hover:scale-[1.02] shadow-xl"
        disabled={loading}
        data-testid="login-button"
        style={{
          background: 'linear-gradient(135deg, #0e67a2 0%, #0a5a8a 100%)',
          boxShadow: '0 8px 24px rgba(25, 118, 210, 0.4), 0 4px 12px rgba(0, 0, 0, 0.1)'
        }}
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
            </svg>
            Signing in...
          </span>
        ) : 'Sign In'}
      </Button>
    </form>
  );

  const renderOtpForm = () => (
    <form onSubmit={handleOtpVerify} className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-bold text-gray-800 mb-2">Enter OTP Code</h3>
        <p className="text-sm text-gray-600 mb-4">
          We&apos;ve sent a 6-digit code to {formData.email}
        </p>
        <Badge variant="outline" className="mb-4 border-[#FF7B00] text-[#FF7B00]">DEV MODE: Check logs below for OTP</Badge>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold text-gray-700" htmlFor="otp">6-Digit OTP Code</label>
        <Input
          id="otp"
          type="text"
          placeholder="123456"
          value={formData.otp}
          onChange={(e) => setFormData({ ...formData, otp: e.target.value })}
          maxLength="6"
          required
          data-testid="otp-input"
          className="text-center text-2xl tracking-widest h-14 border-2 border-gray-200 focus:border-[#0e67a2] rounded-xl"
        />
      </div>

      <div className="flex space-x-3">
        <Button 
          type="button"
          variant="outline"
          onClick={() => setStep('login')}
          className="flex-1 h-12 rounded-xl border-2"
        >
          Back
        </Button>
        <Button 
          type="submit" 
          className="flex-1 h-12 rounded-xl text-white font-bold"
          disabled={loading}
          data-testid="verify-otp-button"
          style={{
            background: 'linear-gradient(135deg, #0e67a2 0%, #0a5a8a 100%)'
          }}
        >
          {loading ? 'Verifying...' : 'Verify OTP'}
        </Button>
      </div>
    </form>
  );

  const renderForgotPasswordForm = () => (
    <form onSubmit={handleForgotPassword} className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-bold text-gray-800 mb-2">Reset Password</h3>
        <p className="text-sm text-gray-600 mb-4">
          Enter your email to receive a reset code
        </p>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold text-gray-700" htmlFor="reset-email">Email</label>
        <Input
          id="reset-email"
          type="email"
          placeholder="your@email.com"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
          className="h-12 border-2 border-gray-200 focus:border-[#0e67a2] rounded-xl"
        />
      </div>

      <div className="flex space-x-3">
        <Button 
          type="button"
          variant="outline"
          onClick={() => setStep('login')}
          className="flex-1 h-12 rounded-xl border-2"
        >
          Back
        </Button>
        <Button 
          type="submit" 
          className="flex-1 h-12 rounded-xl text-white font-bold"
          disabled={loading}
          style={{
            background: 'linear-gradient(135deg, #FF7B00 0%, #E65100 100%)'
          }}
        >
          {loading ? 'Sending...' : 'Send Reset Code'}
        </Button>
      </div>
    </form>
  );

  const renderResetPasswordForm = () => (
    <form onSubmit={handlePasswordReset} className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-bold text-gray-800 mb-2">Set New Password</h3>
        <p className="text-sm text-gray-600 mb-4">
          Enter the OTP code and your new password
        </p>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold text-gray-700" htmlFor="reset-otp">OTP Code</label>
        <Input
          id="reset-otp"
          type="text"
          placeholder="123456"
          value={formData.otp}
          onChange={(e) => setFormData({ ...formData, otp: e.target.value })}
          maxLength="6"
          required
          className="h-12 border-2 border-gray-200 focus:border-[#0e67a2] rounded-xl"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold text-gray-700" htmlFor="new-password">New Password</label>
        <Input
          id="new-password"
          type="password"
          placeholder="••••••••"
          value={formData.newPassword}
          onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
          required
          minLength="8"
          className="h-12 border-2 border-gray-200 focus:border-[#0e67a2] rounded-xl"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold text-gray-700" htmlFor="confirm-password">Confirm Password</label>
        <Input
          id="confirm-password"
          type="password"
          placeholder="••••••••"
          value={formData.confirmPassword}
          onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
          required
          minLength="8"
          className="h-12 border-2 border-gray-200 focus:border-[#0e67a2] rounded-xl"
        />
      </div>

      <div className="flex space-x-3">
        <Button 
          type="button"
          variant="outline"
          onClick={() => setStep('forgot_password')}
          className="flex-1 h-12 rounded-xl border-2"
        >
          Back
        </Button>
        <Button 
          type="submit" 
          className="flex-1 h-12 rounded-xl text-white font-bold"
          disabled={loading}
          style={{
            background: 'linear-gradient(135deg, #0e67a2 0%, #0a5a8a 100%)'
          }}
        >
          {loading ? 'Resetting...' : 'Reset Password'}
        </Button>
      </div>
    </form>
  );

  return (
    <div 
      className="fixed inset-0 overflow-y-auto overflow-x-hidden"
      style={{
        background: '#002c88',
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain'
      }}
    >
      {/* Logo Section - Top Area - Larger and Seamlessly Blended */}
      <div className="pt-6 pb-2 px-2">
        <div className="max-w-lg mx-auto text-center">
          {/* EMZI NEXUS Logo - Large with exact matching background */}
          <div 
            className="mx-auto"
            style={{ 
              width: '100%',
              maxWidth: '380px'
            }}
          >
            <img 
              src="/emzi-nexus-logo.png" 
              alt="EMZI NEXUS" 
              className="w-full h-auto"
              style={{ 
                objectFit: 'contain',
                display: 'block'
              }}
            />
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-md mx-auto px-4 pb-8">
        {step === 'login' && (
          <>
            {/* Login Card */}
            <Card 
              className="w-full border-0 rounded-3xl overflow-hidden" 
              style={{
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 24px rgba(0, 0, 0, 0.2)'
              }} 
              data-testid="real-auth-panel"
            >
              <CardHeader className="text-center space-y-3 pt-8 pb-4 bg-white">
                <CardTitle className="text-2xl font-black text-gray-800">
                  Welcome Back
                </CardTitle>
                <CardDescription className="text-gray-500 text-sm">
                  Sign in to continue to EMZI NEXUS
                </CardDescription>
              </CardHeader>
              
              <CardContent className="px-6 pb-6 bg-white">
                {error && (
                  <Alert variant="destructive" className="mb-4 rounded-xl" data-testid="error-alert">
                    <AlertDescription className="text-sm">{error}</AlertDescription>
                  </Alert>
                )}

                {success && (
                  <Alert className="mb-4 border-green-500 text-green-700 rounded-xl" data-testid="success-alert">
                    <AlertDescription className="text-sm">{success}</AlertDescription>
                  </Alert>
                )}

                {renderLoginForm()}

                {/* Demo Login Buttons - Only shown in development/localhost */}
                {isDevelopment && (
                  <div className="mt-6 pt-6 border-t border-gray-100">
                    <p className="text-xs font-semibold text-gray-400 mb-3 text-center uppercase tracking-wider">
                      Quick Demo Access
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {demoAccounts.map((account) => (
                        <Button
                          key={account.email}
                          type="button"
                          onClick={() => handleDemoLogin(account.email)}
                          disabled={loading}
                          variant="outline"
                          className="text-[#0e67a2] border-[#0e67a2]/30 hover:bg-[#0e67a2]/10 hover:border-[#0e67a2] font-semibold rounded-xl transition-all"
                          data-testid={`demo-${account.role.toLowerCase()}-button`}
                          style={{
                            minHeight: '44px',
                            fontSize: '14px'
                          }}
                        >
                          {account.role}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

              {/* ISO Compliance Badge */}
              <div className="mt-6 text-center">
                <Badge 
                  className="bg-white/20 text-white border-white/30 hover:bg-white/30 px-4 py-1.5 text-xs font-medium"
                >
                  🛡️ ISO Compliance Active
                </Badge>
              </div>
            </>
          )}

          {/* Non-login steps (OTP, Forgot Password, etc.) */}
          {step !== 'login' && (
            <>
              <Card 
                className="w-full border-0 rounded-3xl overflow-hidden" 
                style={{
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 24px rgba(0, 0, 0, 0.2)'
                }} 
                data-testid="auth-step-card"
              >
                <CardContent className="p-6 bg-white">
                  {error && (
                    <Alert variant="destructive" className="mb-4 rounded-xl" data-testid="error-alert">
                      <AlertDescription className="text-sm">{error}</AlertDescription>
                    </Alert>
                  )}

                  {success && (
                    <Alert className="mb-4 border-green-500 text-green-700 rounded-xl" data-testid="success-alert">
                      <AlertDescription className="text-sm">{success}</AlertDescription>
                    </Alert>
                  )}

                  {step === 'otp' && renderOtpForm()}
                  {step === 'forgot_password' && renderForgotPasswordForm()}
                  {step === 'reset_password' && renderResetPasswordForm()}
                </CardContent>
              </Card>

              {/* DEV_LOG Panel */}
              <Card 
                className="w-full border-0 rounded-2xl mt-4 overflow-hidden"
                style={{
                  background: 'rgba(255, 123, 0, 0.15)',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)'
                }}
              >
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-xs font-bold text-[#FF7B00] flex items-center justify-center">
                    📧 DEV_LOG - Email OTP & Auth Events
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="bg-gray-900 text-green-400 p-3 rounded-xl text-xs font-mono max-h-24 overflow-y-auto">
                    {devLogs.slice(-3).map((log, index) => (
                      <div key={`item-${index}`} className="mb-1">
                        {log}
                      </div>
                    ))}
                    {devLogs.length === 0 && (
                      <div className="text-gray-500 italic">Waiting for auth events...</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>

      {/* Footer */}
      <div className="py-4 text-center">
        <p className="text-white/60 text-xs">
          © 2024 EMZI NEXUS. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default Login;
